import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapIcon,
  BellIcon,
  MoonIcon,
  SunIcon,
  Building2Icon,
  LogOutIcon,
  ChevronRightIcon,
  LanguagesIcon,
  UsersIcon,
  XIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentLabel: string;
  onSelectUnit: () => void;
  onOpenMap: () => void;
  onOpenAlarms: () => void;
  hasAlarms?: boolean;
  isAdmin?: boolean;
  onOpenUserManagement?: () => void;
  themePref: 'dark' | 'light' | 'system';
  onThemeToggle: () => void;
  onLogout: () => void;
}

export function MenuSheet({
  isOpen,
  onClose,
  apartmentLabel,
  onSelectUnit,
  onOpenMap,
  onOpenAlarms,
  hasAlarms = false,
  isAdmin = false,
  onOpenUserManagement,
  themePref,
  onThemeToggle,
  onLogout,
}: MenuSheetProps) {
  const { t, i18n } = useTranslation();
  const isDark = themePref === 'dark';

  const toggleLanguage = () => {
    const nextLang = i18n.language.startsWith('ko') ? 'en' : 'ko';
    i18n.changeLanguage(nextLang);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end max-w-md mx-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Drawer Content — 100% Solid Opaque Background for both Light and Dark */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 250 }}
            className={`relative z-10 rounded-t-3xl border-t p-5 pb-8 flex flex-col gap-4 shadow-2xl transition-colors ${
              isDark
                ? 'bg-[#0f172a] text-white border-slate-800'
                : 'bg-white text-slate-900 border-slate-200'
            }`}
            style={{
              backgroundColor: isDark ? '#0f172a' : '#ffffff',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Header */}
            <div className={`flex items-center justify-between pb-2 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <Building2Icon className="w-5 h-5 text-cyan-500" />
                <span className={`font-bold text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {apartmentLabel || 'SED Smart Space'}
                </span>
              </div>
              <button
                onClick={onClose}
                className={`p-1.5 rounded-full transition-colors ${
                  isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'
                }`}
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items List */}
            <div className="flex flex-col gap-2.5">
              {/* Unit Selection */}
              <button
                onClick={() => {
                  onClose();
                  onSelectUnit();
                }}
                className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all text-left active:scale-[0.99] ${
                  isDark
                    ? 'bg-[#1e293b] border-slate-700/80 hover:bg-[#283548]'
                    : 'bg-[#f8fafc] border-slate-200 hover:bg-[#f1f5f9] shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-cyan-500/15 text-cyan-400">
                    <Building2Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      공간 전환
                    </div>
                    <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      다른 공간으로 전환
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>

              {/* Floor Plan / Map View */}
              <button
                onClick={() => {
                  onClose();
                  onOpenMap();
                }}
                className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all text-left active:scale-[0.99] ${
                  isDark
                    ? 'bg-[#1e293b] border-slate-700/80 hover:bg-[#283548]'
                    : 'bg-[#f8fafc] border-slate-200 hover:bg-[#f1f5f9] shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-emerald-500/15 text-emerald-400">
                    <MapIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      공간 맵
                    </div>
                    <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      공간 맵 기반 기기 및 환경 확인
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>

              {/* Alarms */}
              <button
                onClick={() => {
                  onClose();
                  onOpenAlarms();
                }}
                className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all text-left active:scale-[0.99] ${
                  isDark
                    ? 'bg-[#1e293b] border-slate-700/80 hover:bg-[#283548]'
                    : 'bg-[#f8fafc] border-slate-200 hover:bg-[#f1f5f9] shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-amber-500/15 text-amber-400 relative">
                    <BellIcon className="w-4 h-4" />
                    {hasAlarms && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]" />
                    )}
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      알림 및 이벤트
                    </div>
                    <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      실시간 경보 및 히스토리
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>

              {/* User & RBAC Management (Admin Only) */}
              {isAdmin && onOpenUserManagement && (
                <button
                  onClick={() => {
                    onClose();
                    onOpenUserManagement();
                  }}
                  className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all text-left active:scale-[0.99] ${
                    isDark
                      ? 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20'
                      : 'bg-purple-50 border-purple-200 hover:bg-purple-100 shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-purple-500/20 text-purple-400">
                      <UsersIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className={`text-sm font-semibold ${isDark ? 'text-purple-200' : 'text-purple-900'}`}>
                        사용자 및 권한 관리
                      </div>
                      <div className={`text-xs ${isDark ? 'text-purple-300/70' : 'text-purple-600'}`}>
                        회원가입 승인 및 공간별 권한 지정
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                </button>
              )}

              {/* Theme & Language Quick Settings */}
              <div className="grid grid-cols-2 gap-2 mt-1">
                {/* Theme Toggle */}
                <button
                  type="button"
                  onClick={onThemeToggle}
                  className={`flex items-center justify-between p-3 rounded-2xl border transition-all active:scale-95 ${
                    isDark
                      ? 'bg-[#1e293b] border-slate-700/80 text-white hover:bg-[#283548]'
                      : 'bg-[#f8fafc] border-slate-200 text-slate-800 hover:bg-[#f1f5f9] shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isDark ? (
                      <MoonIcon className="w-4 h-4 text-indigo-400" />
                    ) : (
                      <SunIcon className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-xs font-semibold">
                      {isDark ? '다크 모드' : '라이트 모드'}
                    </span>
                  </div>
                </button>

                {/* Language Toggle */}
                <button
                  type="button"
                  onClick={toggleLanguage}
                  className={`flex items-center justify-between p-3 rounded-2xl border transition-all active:scale-95 ${
                    isDark
                      ? 'bg-[#1e293b] border-slate-700/80 text-white hover:bg-[#283548]'
                      : 'bg-[#f8fafc] border-slate-200 text-slate-800 hover:bg-[#f1f5f9] shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <LanguagesIcon className="w-4 h-4 text-cyan-500" />
                    <span className="text-xs font-semibold">
                      {i18n.language.startsWith('ko') ? '한국어' : 'English'}
                    </span>
                  </div>
                </button>
              </div>

              {/* Logout */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onLogout();
                }}
                className={`flex items-center justify-center gap-2 p-3 mt-2 rounded-2xl border font-semibold text-xs transition-all active:scale-[0.98] ${
                  isDark
                    ? 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'
                    : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100 shadow-sm'
                }`}
              >
                <LogOutIcon className="w-4 h-4" />
                <span>로그아웃</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
